import React, {Fragment, useContext} from 'react';

/* Atoms */
import { Button } from 'js/components/atoms';
import { Icon } from 'js/components/atoms';

/* Molecules */
import CardLabel from "./CardLabel";

/* React Bootstrap Tooltip */
import {Tooltip} from "react-bootstrap";
import {OverlayTrigger} from "react-bootstrap";

/* Symbols */
import buttonIcon from 'symbols/arrow.svg';
import toolTip from 'symbols/tooltip.svg';

/* Context */
import vehicleOverviewContext from "../../context/VehicleOverviewContext";
import productPreviewContext from "../../context/ProductPreviewContext";

/* Utilities */
import { trans, transWithFilter, currency } from 'js/utilities';

export default function CardVehicle({
    images = [],
    title,
    text,
    specs = [],
    prices = [],
    button,
    isPartnerVehicle,
    enrichedValues = {},
    datalayerActiveFilters
}) {
    const vOContext = useContext(vehicleOverviewContext) ? useContext(vehicleOverviewContext) : useContext(productPreviewContext) ? useContext(productPreviewContext) : null
    const surchargeConversionMap = {
        'cooling_surcharge_price': 'koelwagen',
        'transfer_surcharge_price': 'overbrengvoertuig',
        'mourning_surcharge_price': 'rouwvoertuig',
        'multi_temp_surcharge_price': 'multitempwagen'
    };
    const surchargeActiveConversionRuleMap = {
        'enriched_mourning_surcharge_set': ['rouwvoertuig'],
        'enriched_transfer_surcharge_set': ['overbrengvoertuig'],
        'enriched_cooling_surcharge_set': ['koelwagen'],
        'enriched_multi_temp_surcharge_set': ['multitempwagen'],
        'enriched_mourning_or_transfer_surcharge_set': ['rouwvoertuig', 'overbrengvoertuig'],
        'enriched_cooling_or_multi_temp_surcharge_set': ['koelwagen', 'multitempwagen'],
    }

    return (
        <div className="card card--link card--bordered card--vehicle">
            <div className="card__image">
                <div className="card__labels d-flex flex-wrap gap-2">
                    {isPartnerVehicle && vOContext ?
                        <CardLabel
                            style={{backgroundColor: vOContext.partnersAccentColor ? vOContext.partnersAccentColor : 'inherit'}}
                            labelTitle={trans('partner.partner_vehicle')}
                            onClick={() => {
                                vOContext.partnersModalOpen(true);
                            }}
                        />
                    : null}
                    {['cooling_surcharge_price', 'transfer_surcharge_price', 'mourning_surcharge_price', 'multi_temp_surcharge_price'].map((surchargePrice) => {
                        const conversionType = surchargeConversionMap[surchargePrice];

                        // Toon alleen de ombouwvorm die in de voertuiglijst is ingesteld.
                        if ((enrichedValues[surchargePrice] > 0) && vOContext.useConversionVehicleList && vOContext.conversionRuleType) {
                            const conversionTypeToShow = surchargeActiveConversionRuleMap[vOContext.conversionRuleType];
                            if (!conversionTypeToShow.find(type => type === conversionType)) return null;
                        }

                        if ((enrichedValues[surchargePrice] <= 0 || !conversionType) || !vOContext.useConversionVehicleList) return null;

                        return (
                            vOContext.conversionForms &&
                            vOContext.conversionForms
                                .filter((conversionForm) => conversionForm.conversion_type === conversionType)
                                .map((conversionForm) => (
                                    <CardLabel
                                        key={`${conversionForm.conversion_type}-${surchargePrice}`}
                                        style={{ backgroundColor: conversionForm.accent_color || 'inherit' }}
                                        labelTitle={conversionForm?.top_title}
                                        onClick={() => {
                                            vOContext.conversionFormModalOpen(conversionForm.conversion_type);
                                        }}
                                    />
                                ))
                        );
                    })}
                </div>
                {prices?.map((price, index) => (
                    <Fragment key={index}>
                        {(price?.from && price.from !== price.price) ? (
                            <span className="card__label">
                                <p className="font-weight-bold px-2 text-white">{currency(price.from - price.price)}{price.suffix} {trans(`sulu.vehicle.card.price.profit.title`)}</p>
                            </span>
                        ) : null}
                    </Fragment>
                ))}
                <img
                    {...{
                        src: '',
                        alt: title || 'Voertuig afbeelding',
                    }}
                    {...images[0]}
                    className="img-fluid card__image"
                />
            </div>
            <div className="card-body">
                {title ? (
                    <h6 className="card__title mb-1">
                        {title}
                    </h6>
                ) : null}
                {text ? (
                    <p className="text-sm text-truncate mb-2">
                        {text}
                    </p>
                ) : null}
                <div>
                    {specs?.length > 0 ? (
                        <div className="card__specs align-items-center">
                            {specs?.map((spec, index) => (
                                <Fragment key={index}>
                                    {index > 0 ? (
                                        <div className="border-left"></div>
                                    ) : null}
                                    <p className="card__spec text--14">
                                        {transWithFilter(`app.vehicle.specs.${spec}`, {}, spec)}
                                    </p>
                                </Fragment>
                            ))}
                        </div>
                    ) : null}
                    <hr className="my-4" />
                    <div className="card__prices">
                        {prices?.map((price, index) => (
                            <div key={index} className="card__price">
                                <div className="d-flex gap-1 align-items-center">
                                    <span className="card__price-title text-muted--80">{trans(`sulu.vehicle.card.price.${price.type}.title`)}</span>
                                    {price?.label ?
                                        <span className="card__price-label text--14 text-muted--60">{price.label}</span>
                                    : null}
                                    {(price?.from && price.from !== price.price) ? (
                                        <span className="card__price-from">{currency(price.from)}{price.suffix}</span>
                                    ) : null}
                                </div>
                                <div className="d-flex gap-2 align-items-center">
                                    <span className="card__price-value font-weight-bold">{currency(price.price)}{price.suffix}</span>

                                    {trans(`sulu.vehicle.card.price.${price.type}.tooltip`) ? (
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip className="mb-n2">
                                                    {trans(`sulu.vehicle.card.price.${price.type}.tooltip`)}
                                                </Tooltip>
                                            }
                                        >
                                            <div className="btn--tooltip">
                                                <Icon
                                                    theme=""
                                                    size={20}
                                                    url={toolTip}
                                                />
                                            </div>
                                        </OverlayTrigger>
                                    ) : (
                                        <Icon
                                            theme="base"
                                            size={20}
                                            url={toolTip}
                                            className="text-muted--0"
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="mt-auto">
                    <Button
                        {...{
                            text: trans('sulu.vehicle.card.button.title'),
                            theme: 'outline-primary',
                            className: 'btn-block mt-4 stretched-link',
                            icon: buttonIcon,
                        }}
                        onClick={() => {
                            window.dataLayer = window.dataLayer || [];
                            window.dataLayer.push({
                                event: 'filter',
                                filter_id: Date.now(),
                                ...datalayerActiveFilters
                            });
                        }}
                        {...button}
                    />
                </div>
            </div>
        </div>

    );
}
